import { useMemo, useState } from 'react';
import { FiUsers } from 'react-icons/fi';

import { BackofficeUserInfo, UserOutput } from '../../../models/UserModels';
import { useAdminApi } from '../Admin/hooks/useAdminApi';
import Button from '../../../components/Atoms/Button';
import {
  PageHeader,
  EmptyState,
  ErrorState,
  SkeletonTable,
  SearchField,
  useAdminResource,
} from '../../../components/Admin';

import { ListContainer } from '../../_layout/AdminLayout.styles';
import UsersTable, { isVerified } from './components/UsersTable';

type VerifiedFilter = 'all' | 'yes' | 'no';

const VERIFIED_OPTIONS = [
  { value: 'all', label: 'Todos os e-mails' },
  { value: 'yes', label: 'E-mail verificado' },
  { value: 'no', label: 'E-mail não verificado' },
];

const Users = () => {
  const { fetchUsers } = useAdminApi();

  const { data, isLoading, loadError, hasMore, reload, loadMore } = useAdminResource<
    BackofficeUserInfo,
    UserOutput
  >({
    fetchPage: page => fetchUsers(page),
    select: response => response.users ?? [],
    selectPages: response => response.pages ?? 0,
    errorMessage: 'Não foi possível buscar a lista de usuários.',
  });

  const [search, setSearch] = useState('');
  const [verified, setVerified] = useState<VerifiedFilter>('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase();
    return data.filter(({ user }) => {
      if (verified !== 'all' && isVerified(user) !== (verified === 'yes')) return false;
      if (!q) return true;
      return (
        (user.fullname ?? '').toLocaleLowerCase().includes(q) ||
        (user.email ?? '').toLocaleLowerCase().includes(q)
      );
    });
  }, [data, search, verified]);

  const showInitialLoading = isLoading && data.length === 0 && !loadError;

  return (
    <>
      <PageHeader title="Usuários" subtitle="Alunos cadastrados na escola." />

      <ListContainer>
        {showInitialLoading ? (
          <SkeletonTable rows={6} columns={4} />
        ) : loadError && data.length === 0 ? (
          <ErrorState message={loadError} onRetry={reload} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={<FiUsers size={40} />}
            title="Nenhum usuário cadastrado"
            description="Os alunos aparecem aqui assim que criam uma conta."
          />
        ) : (
          <>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Buscar por nome ou e-mail..."
              label="Buscar usuário por nome ou e-mail"
              filter={{
                value: verified,
                label: 'Filtrar por verificação de e-mail',
                options: VERIFIED_OPTIONS,
                onChange: value => setVerified(value as VerifiedFilter),
              }}
              count={
                hasMore
                  ? `${filtered.length} de ${data.length} carregados`
                  : `${filtered.length} de ${data.length} usuários`
              }
            />
            {filtered.length === 0 ? (
              <EmptyState
                icon={<FiUsers size={40} />}
                title="Nenhum usuário encontrado"
                description={
                  hasMore
                    ? 'A busca só considera os usuários já carregados. Carregue mais para ampliar.'
                    : 'Tente ajustar a busca ou o filtro.'
                }
              />
            ) : (
              <UsersTable data={filtered} />
            )}
          </>
        )}
      </ListContainer>

      {hasMore && data.length > 0 && (
        <Button onClick={loadMore} loading={isLoading}>
          Carregar mais usuários
        </Button>
      )}
    </>
  );
};

export default Users;
